<template>
  <div class="lo-site-page">
    <!-- Teleports into the app top bar under sidebar nav, renders in place otherwise.
         Same wrapper every other 2.0 page uses (PccdDashboard, Leaderboard, Applications). -->
    <PageHeaderPortal>
      <div class="page-header-left">
        <div class="page-icon">
          <i class="fa-duotone fa-globe"></i>
        </div>

        <div>
          <h1 class="page-title">My Website</h1>
          <p class="page-subtitle">Choose the design used for your loan officer site</p>
        </div>
      </div>
    </PageHeaderPortal>

    <div class="lo-site-body">
      <div v-if="!lar" class="state-card state-card--error">
        <i class="fa-duotone fa-circle-exclamation state-card__icon"></i>

        <div class="state-card__text">
          <div class="state-card__title">We couldn't read your LAR</div>
          <div class="state-card__message">
            Your user profile doesn't have a LAR on it, so we can't match you to a website.
          </div>
        </div>
      </div>

      <div v-else-if="isCheckingLoPage" class="state-card">
        <i class="fa-duotone fa-spinner-third fa-spin state-card__icon"></i>

        <div class="state-card__text">
          <div class="state-card__title">Checking website availability</div>
          <div class="state-card__message">
            Verifying that an active loan officer website exists for your profile.
          </div>
        </div>
      </div>

      <div v-else-if="loPageCheckFailed" class="state-card state-card--error">
        <i class="fa-duotone fa-triangle-exclamation state-card__icon"></i>

        <div class="state-card__text">
          <div class="state-card__title">Couldn't verify your website</div>
          <div class="state-card__message">
            Something went wrong reaching the site service. Try again in a few minutes.
          </div>
        </div>
      </div>

      <div v-else-if="hasLoPage === false" class="state-card state-card--warning">
        <i class="fa-duotone fa-circle-info state-card__icon"></i>

        <div class="state-card__text">
          <div class="state-card__title">No website yet</div>
          <div class="state-card__message">
            We couldn't find an active loan officer website for your LAR. Style selection unlocks
            once one has been created.
          </div>
        </div>
      </div>

      <template v-else-if="hasLoPage === true">
        <section class="lo-card">
          <header class="lo-card__head">
            <span class="lo-card__title">
              <i class="fa-duotone fa-palette"></i>
              Website Style
            </span>

            <span class="lo-card__meta">
              {{ templates.length }} design{{ templates.length === 1 ? '' : 's' }} available
            </span>
          </header>

          <div class="lo-card__body">
            <div class="template-selector">
              <button v-for="template in templates"
                      :key="template.id"
                      type="button"
                      class="template-option"
                      :class="{
                                active: previewTemplate?.id === template.id,
                                saved: savedTemplate?.id === template.id
                            }"
                      @click="selectPreview(template)">
                <span class="template-number">
                  {{ String(template.concept).padStart(2, '0') }}
                </span>

                <span class="template-option-content">
                  <span class="template-name">
                    {{ template.name }}
                  </span>

                  <span class="template-description">
                    {{ template.shortDescription }}
                  </span>
                </span>

                <span v-if="savedTemplate?.id === template.id"
                      class="lo-pill lo-pill--current">
                  Current
                </span>
              </button>
            </div>
          </div>
        </section>

        <section v-if="previewTemplate" class="lo-card">
          <header class="lo-card__head">
            <span class="preview-selection">
              <span class="preview-label">Previewing</span>
              <span class="preview-name">{{ previewTemplate.name }}</span>
              <span class="preview-url">{{ selectedUrl }}</span>
            </span>

            <span class="preview-actions">
              <button type="button"
                      class="lo-btn lo-btn--icon"
                      title="Reload preview"
                      @click="reloadPreview">
                <i class="fa-duotone fa-arrows-rotate"></i>
              </button>

              <button v-if="savedTemplate?.id !== previewTemplate.id"
                      type="button"
                      class="lo-btn lo-btn--primary"
                      :disabled="isSaving"
                      @click="saveSelection">
                {{ isSaving ? 'Saving…' : 'Use This Website' }}
              </button>

              <span v-else class="lo-pill lo-pill--current">
                <i class="fa-solid fa-check"></i>
                Current Website
              </span>
            </span>
          </header>

          <div v-if="lar && previewUrl" class="lo-card__body">
            <div class="browser-frame">
              <div class="browser-toolbar">
                <span class="browser-controls">
                  <span></span>
                  <span></span>
                  <span></span>
                </span>

                <span class="browser-address">
                  <i class="fa-solid fa-lock lock-icon"></i>
                  <span>{{ selectedUrl }}</span>
                </span>
              </div>

              <div class="browser-content">
                <iframe :key="iframeKey"
                        :src="previewUrl"
                        :title="`${previewTemplate.name} Website Preview`"
                        class="preview-iframe"
                        @load="handlePreviewLoaded"></iframe>
              </div>
            </div>
          </div>
        </section>
      </template>
    </div>

    <transition name="dialogfade">
      <Dialog v-if="showErrorDialog"
              :title="errorTitle"
              class="lo-site-error-dialog"
              @close="closeErrorDialog">
        <p class="error-dialog-message">
          {{ errorMsg }}
        </p>
      </Dialog>
    </transition>

    <NotificationGroup class="lo-site-notification-group">
      <Fade :appear="successNotification">
        <Notification v-if="successNotification"
                      :type="{ style: 'success', icon: true }"
                      :closable="true"
                      @close="closeSuccessNotification">
          <span>{{ notificationMsg }}</span>
        </Notification>
      </Fade>
    </NotificationGroup>
  </div>
</template>

<script setup>
  import { computed, onBeforeUnmount, onMounted, ref } from 'vue'
  import { Dialog } from '@progress/kendo-vue-dialogs'
  import { Notification, NotificationGroup } from '@progress/kendo-vue-notification'
  import { Fade } from '@progress/kendo-vue-animation'
  import PageHeaderPortal from '@/views/PageHeaderPortal.vue'
  import apiService from '../api/api.service.js'
  import auth from '../authConfig.js'
  // The URL persisted to LoSiteRedirect must always point to the public Gold Star site,
  // regardless of whether SwiftProcessingSPA is running locally, in Development, or Production.
  const redirectBaseUrl = 'https://goldstarfinancial.com/lo-sites'
  const defaultLoSiteBaseUrl = 'https://lo-sites.goldstarfinancial.com/'

  // Local development can still use the locally running CorpSiteV2 instance for the iframe preview.
  // Non-local builds preview the public site.  http://localhost:3000/lo-sites
  const previewBaseUrl =
    process.env.VUE_APP_LO_SITE_PREVIEW_URL ||
    'https://goldstarfinancial.com/lo-sites'

  const lar = ref('')
  const azureId = ref('')

  const hasLoPage = ref(null)
  const isCheckingLoPage = ref(false)
  const loPageCheckFailed = ref(false)

  const previewTemplate = ref(null)
  const savedTemplate = ref(null)

  const isSaving = ref(false)
  const iframeKey = ref(0)

  const successNotification = ref(false)
  const notificationMsg = ref('')
  const showErrorDialog = ref(false)
  const errorTitle = ref('')
  const errorMsg = ref('')

  let notificationTimer = null

  const templates = [
    {
      id: 0,
      concept: 0,
      name: 'Default LO Site',
      isDefault: true,
      shortDescription: 'Original Gold Star Loan Officer website'
    },
    {
      id: 1,
      concept: 1,
      name: 'Editorial',
      slug: 'editorial',
      shortDescription: 'Polished corporate layout'
    },
    {
      id: 2,
      concept: 2,
      name: 'Simple',
      slug: 'simple',
      shortDescription: 'Clean single-page experience'
    },
    {
      id: 3,
      concept: 3,
      name: 'Portrait Split',
      slug: 'portrait',
      shortDescription: 'Personal, profile-focused design'
    },
    {
      id: 4,
      concept: 4,
      name: 'Warm Hospitality',
      slug: 'hospitality',
      shortDescription: 'Welcoming and relationship driven'
    },
    {
      id: 5,
      concept: 5,
      name: 'Modern Speed',
      slug: 'speed',
      shortDescription: 'Fast, modern and energetic'
    },
    {
      id: 6,
      concept: 6,
      name: 'Division Brand',
      slug: 'brand',
      shortDescription: 'Styled around division branding'
    }
  ]

  const selectedUrl = computed(() => {
    return buildLoSiteUrl(previewTemplate.value, redirectBaseUrl)
  })

  const previewUrl = computed(() => {
    const url = buildLoSiteUrl(previewTemplate.value, previewBaseUrl)

    if (!url) {
      return ''
    }

    if (previewTemplate.value?.isDefault) {
      return url
    }

    return `${url}?embeddedPreview=true`
  })

  onMounted(async () => {
    loadCurrentUser()

    if (!lar.value || !azureId.value) {
      return
    }

    await loadLoPageStatus()

    if (hasLoPage.value !== true) {
      return
    }

    await loadCurrentSelection()
  })

  onBeforeUnmount(() => {
    if (notificationTimer) {
      clearTimeout(notificationTimer)
    }
  })

  function loadCurrentUser() {
    try {
      const userAzureData = JSON.parse(
        localStorage.getItem('user_azure_data') || '{}'
      )

      azureId.value = userAzureData?.id ?? ''

      lar.value =
        userAzureData
          ?.onPremisesExtensionAttributes
          ?.extensionAttribute1 ?? ''

      if (!lar.value) {
        console.error(
          'extensionAttribute1 was not found in user_azure_data.'
        )

        return
      }
    } catch (error) {
      console.error(
        'Unable to load user_azure_data from localStorage.',
        error
      )
    }
  }

  async function loadLoPageStatus() {
    isCheckingLoPage.value = true
    loPageCheckFailed.value = false
    hasLoPage.value = null

    try {
      const tokenResponse = await auth.acquireTokenResponse()

      if (!tokenResponse?.accessToken) {
        throw new Error('Unable to acquire an authentication token.')
      }

      const response = await apiService.HasLoPage(
        lar.value,
        tokenResponse.accessToken
      )

      const isSuccess = response?.data?.isSuccess ?? response?.data?.issuccess

      if (isSuccess === false) {
        throw new Error(
          response?.data?.message ||
          'Unable to determine LO website availability.'
        )
      }

      hasLoPage.value = response?.data?.data === true

      // if (!hasLoPage.value) {
      //   previewTemplate.value = null
      //   savedTemplate.value = null

      //   showRequestError(
      //     'LO Website Not Available',
      //     'We could not find an active Loan Officer website associated with your profile. Website style selection is unavailable until an LO page has been created for your LAR.'
      //   )
      // }
    } catch (error) {
      console.error(
        'Unable to determine LO website availability.',
        error
      )

      hasLoPage.value = null
      loPageCheckFailed.value = true
      previewTemplate.value = null
      savedTemplate.value = null

      showRequestError(
        'Unable to Check Website Availability',
        getErrorMessage(
          error,
          'We could not verify whether an active Loan Officer website is available for your profile. Please try again.'
        )
      )
    } finally {
      isCheckingLoPage.value = false
    }
  }

  async function loadCurrentSelection() {
    try {
      const tokenResponse = await auth.acquireTokenResponse()

      if (!tokenResponse?.accessToken) {
        throw new Error('Unable to acquire an authentication token.')
      }

      const response = await apiService.GetLoBypassURL(
        azureId.value,
        tokenResponse.accessToken
      )

      const isSuccess = response?.data?.isSuccess ?? response?.data?.issuccess

      if (isSuccess === false) {
        throw new Error(
          response?.data?.message ||
          'Unable to load your current LO website selection.'
        )
      }

      const redirect = response?.data?.data
      const redirectUrl = redirect?.redirectUrl ?? redirect?.redirecturl

      if (!redirectUrl) {
        savedTemplate.value = templates[0]
        previewTemplate.value = templates[0]

        showSuccessNotification(
          'Default LO Site is your current website.'
        )

        return
      }

      const currentTemplate = findTemplateByRedirectUrl(redirectUrl)

      if (!currentTemplate) {
        previewTemplate.value = templates[0]

        showRequestError(
          'Website Selection Error',
          'Your saved website redirect was returned successfully, but it does not match one of the available website styles.'
        )

        return
      }

      savedTemplate.value = currentTemplate
      previewTemplate.value = currentTemplate

      showSuccessNotification(
        `${currentTemplate.name} is your current website.`
      )
    } catch (error) {
      console.error(
        'Unable to load the current LO website selection.',
        error
      )

      previewTemplate.value = templates[0]

      showRequestError(
        'Unable to Load Current Website',
        getErrorMessage(
          error,
          'We could not load your current website selection. Please try again.'
        )
      )
    }
  }

  function findTemplateByRedirectUrl(redirectUrl) {
    if (!redirectUrl) {
      return templates[0]
    }

    try {
      const url = new URL(redirectUrl)
      const pathParts = url.pathname.split('/').filter(Boolean)

      if (url.hostname.toLowerCase() === 'lo-sites.goldstarfinancial.com') {
        return templates[0]
      }

      if (pathParts[0]?.toLowerCase() === 'lo-sites') {
        const slug = pathParts[1]?.toLowerCase()

        return templates.find(template =>
          template.slug?.toLowerCase() === slug
        ) ?? null
      }
    } catch (error) {
      console.warn('Unable to parse saved LO redirect URL.', error)
    }

    return null
  }

  function buildLoSiteUrl(template, baseUrl) {
    if (!template || !lar.value) {
      return ''
    }

    if (template.isDefault) {
      return `${defaultLoSiteBaseUrl}?lar=${encodeURIComponent(lar.value)}`
    }

    if (!template.slug || !baseUrl) {
      return ''
    }

    return `${baseUrl}/${template.slug}/${encodeURIComponent(lar.value)}`
  }

  function selectPreview(template) {
    previewTemplate.value = template

    console.log(
      'LO Site Preview URL:',
      template.isDefault
        ? buildLoSiteUrl(template, previewBaseUrl)
        : `${buildLoSiteUrl(template, previewBaseUrl)}?embeddedPreview=true`
    )

    console.log(
      'LO Site Redirect URL:',
      buildLoSiteUrl(template, redirectBaseUrl)
    )
  }

  function reloadPreview() {
    iframeKey.value++
  }

  function handlePreviewLoaded() {
    // console.log(
    //   'Preview iframe loaded:',
    //   previewUrl.value
    // )
  }

  async function saveSelection() {
    if (!previewTemplate.value || !selectedUrl.value || !azureId.value || !lar.value) {
      return
    }

    isSaving.value = true

    try {
      const tokenResponse = await auth.acquireTokenResponse()

      if (!tokenResponse?.accessToken) {
        throw new Error('Unable to acquire an authentication token.')
      }

      let response

      if (previewTemplate.value.isDefault) {
        response = await apiService.DeleteLoBypassURL(
          azureId.value,
          tokenResponse.accessToken
        )
      } else {
        const request = {
          azureId: azureId.value,
          lar: lar.value,
          redirectUrl: selectedUrl.value
        }

        response = await apiService.UpdateLoBypassURL(
          request,
          tokenResponse.accessToken
        )
      }

      const isSuccess = response?.data?.isSuccess ?? response?.data?.issuccess

      if (isSuccess === false) {
        throw new Error(
          response?.data?.message ||
          'Unable to save LO website selection.'
        )
      }

      savedTemplate.value = previewTemplate.value

      if (previewTemplate.value.isDefault) {
        showSuccessNotification(
          'Default LO Site restored successfully. Your custom redirect has been removed.'
        )
      } else {
        showSuccessNotification(
          `${previewTemplate.value.name} is now your current website.`
        )
      }
    } catch (error) {
      console.error(
        'Unable to save LO website selection.',
        error
      )

      showRequestError(
        previewTemplate.value?.isDefault
          ? 'Unable to Restore Default Website'
          : 'Unable to Update Website',
        getErrorMessage(
          error,
          'We could not update your website selection. Please try again.'
        )
      )
    } finally {
      isSaving.value = false
    }
  }

  function showSuccessNotification(message) {
    notificationMsg.value = message
    successNotification.value = true

    if (notificationTimer) {
      clearTimeout(notificationTimer)
    }

    notificationTimer = setTimeout(() => {
      successNotification.value = false
      notificationTimer = null
    }, 4000)
  }

  function closeSuccessNotification() {
    successNotification.value = false

    if (notificationTimer) {
      clearTimeout(notificationTimer)
      notificationTimer = null
    }
  }

  function showRequestError(title, message) {
    errorTitle.value = title
    errorMsg.value = message
    showErrorDialog.value = true
  }

  function closeErrorDialog() {
    showErrorDialog.value = false
    errorTitle.value = ''
    errorMsg.value = ''
  }

  function getErrorMessage(error, fallbackMessage) {
    return (
      error?.response?.data?.message ||
      error?.response?.data?.data ||
      error?.message ||
      fallbackMessage
    )
  }

</script>

<style scoped>
  /* ── swiftapp 2.0 ──────────────────────────────────────────────────────────
     Page shell + controls follow PccdDashboard; the card follows the newest
     condition-command trend cards. Values are px on purpose: the rem root in
     cgslight.css is fluid (7/8/9/10px by breakpoint), so rem sizes silently
     shrink below 1440px. The old dark hero is gone — this page now sits on the
     same ground as every other page.
     ────────────────────────────────────────────────────────────────────────── */

  .lo-site-page,
  .lo-site-page * {
    box-sizing: border-box;
  }

  .lo-site-page {
    display: flex;
    flex-direction: column;
    width: 100%;
    min-width: 0;
    padding: 18px;
    background: #f7f8fb;
    color: #1f2a44;
  }

  /* ── Page header (teleported into the top bar by PageHeaderPortal) ── */
  .page-header-left {
    display: flex;
    align-items: center;
    gap: 14px;
  }

  .page-icon {
    width: 48px;
    height: 48px;
    border-radius: 5px;
    background: #123a71;
    color: #fff;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 20px;
    flex: 0 0 auto;
  }

  .page-title {
    margin: 0;
    font-size: 20px;
    font-weight: 700;
    color: #1f2a44;
    letter-spacing: -.01em;
  }

  .page-subtitle {
    margin: 4px 0 0;
    font-size: 13px;
    color: #7b8397;
    font-weight: 500;
  }

  .lo-site-body {
    display: flex;
    flex-direction: column;
    gap: 12px;
    min-width: 0;
  }

  /* ── Card ── */
  .lo-card {
    background: #fff;
    border: 1px solid #eaeaea;
    border-radius: 12px;
    overflow: hidden;
    box-shadow: 0 1px 0 rgba(0, 0, 0, .02);
    display: flex;
    flex-direction: column;
    min-width: 0;
  }

  .lo-card__head {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    padding: 10px 12px;
    border-bottom: 1px solid #eaeaea;
    background: rgba(5, 29, 58, .02);
    min-width: 0;
  }

  .lo-card__title {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    font-size: 13px;
    font-weight: 950;
    color: #1f2a44;
  }

  .lo-card__title i {
    color: #5f79c7;
    font-size: 14px;
  }

  .lo-card__meta {
    font-size: 12px;
    font-weight: 800;
    color: #7b8397;
    white-space: nowrap;
  }

  .lo-card__body {
    padding: 12px;
    min-width: 0;
  }

  /* ── Template picker ──
     auto-fill rather than a hard repeat(4): seven templates over four columns
     always left a ragged second row. */
  .template-selector {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(230px, 1fr));
    gap: 10px;
    min-width: 0;
  }

  .template-option {
    position: relative;
    display: flex;
    gap: 10px;
    min-width: 0;
    padding: 12px;
    text-align: left;
    font-family: inherit;
    color: #1f2a44;
    background: #fff;
    border: 1px solid #e7ebf2;
    border-radius: 12px;
    cursor: pointer;
    transition: transform 140ms ease, box-shadow 140ms ease, border-color 140ms ease;
  }

  .template-option:hover {
    transform: translateY(-1px);
    border-color: rgba(15, 23, 42, .14);
    box-shadow: 0 6px 16px rgba(0, 0, 0, .06);
  }

  .template-option:focus-visible {
    outline: none;
    border-color: #123a71;
    box-shadow: 0 0 0 3px rgba(18, 58, 113, .18);
  }

  /* Selected reads as "chosen" (ring), not as "inverted" the way the old
     white-on-navy treatment did. */
  .template-option.active {
    border-color: #123a71;
    box-shadow: 0 0 0 2px rgba(18, 58, 113, .10);
  }

  .template-number {
    flex: 0 0 auto;
    padding-top: 1px;
    font-size: 11px;
    font-weight: 700;
    letter-spacing: .08em;
    color: #7b8397;
    font-variant-numeric: tabular-nums;
  }

  .template-option-content {
    display: block;
    min-width: 0;
  }

  .template-name {
    display: block;
    margin-bottom: 3px;
    font-size: 13px;
    font-weight: 700;
    color: #1f2a44;
  }

  .template-description {
    display: block;
    font-size: 12px;
    line-height: 1.45;
    color: #7b8397;
  }

  /* ── Pill ── */
  .lo-pill {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 3px 9px;
    border-radius: 999px;
    border: 1px solid transparent;
    font-size: 11px;
    font-weight: 700;
    white-space: nowrap;
  }

  .lo-pill--current {
    background: #eef9f0;
    border-color: #d9eedf;
    color: #2c7a4b;
  }

  .template-option .lo-pill {
    position: absolute;
    top: 10px;
    right: 10px;
  }

  /* Only the saved card carries the pill, so only it gives up the gutter. */
  .template-option.saved {
    padding-right: 76px;
  }

  /* ── Preview toolbar, living in the card head ── */
  .preview-selection {
    display: flex;
    align-items: baseline;
    gap: 10px;
    min-width: 0;
  }

  .preview-label {
    flex: 0 0 auto;
    font-size: 11px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: .08em;
    color: #7b8397;
  }

  .preview-name {
    flex: 0 0 auto;
    font-size: 13px;
    font-weight: 950;
    color: #1f2a44;
  }

  .preview-url {
    min-width: 0;
    font-size: 11.5px;
    color: #7b8397;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .preview-actions {
    display: flex;
    align-items: center;
    gap: 8px;
    flex: 0 0 auto;
  }

  /* ── Buttons ── */
  .lo-btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 7px;
    height: 32px;
    padding: 0 14px;
    border: 1px solid #d9dfeb;
    border-radius: 5px;
    background: #fff;
    color: #22314f;
    font-family: inherit;
    font-size: 12px;
    font-weight: 600;
    cursor: pointer;
    transition: background .15s, border-color .15s;
  }

  .lo-btn:hover:not(:disabled) {
    background: #f4f7fc;
    border-color: #cfd8e8;
  }

  .lo-btn:disabled {
    opacity: .55;
    cursor: not-allowed;
  }

  .lo-btn--primary {
    background: #17315c;
    border-color: #17315c;
    color: #fff;
  }

  .lo-btn--primary:hover:not(:disabled) {
    background: #1d3d70;
    border-color: #1d3d70;
  }

  .lo-btn--icon {
    width: 32px;
    padding: 0;
  }

  /* ── Browser frame around the live preview ── */
  .browser-frame {
    border: 1px solid #e7ebf2;
    border-radius: 10px;
    overflow: hidden;
    background: #fff;
  }

  .browser-toolbar {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 8px 10px;
    background: #fafbfd;
    border-bottom: 1px solid #e9edf3;
  }

  .browser-controls {
    display: flex;
    gap: 5px;
    flex: 0 0 auto;
  }

  .browser-controls span {
    width: 9px;
    height: 9px;
    border-radius: 99px;
    background: #dfe4ee;
  }

  .browser-address {
    flex: 1 1 auto;
    min-width: 0;
    display: flex;
    align-items: center;
    gap: 7px;
    height: 26px;
    padding: 0 10px;
    border-radius: 999px;
    background: #fff;
    border: 1px solid rgba(15, 23, 42, .10);
    font-size: 11.5px;
    color: rgba(15, 23, 42, .62);
  }

  .browser-address span:last-child {
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .lock-icon {
    flex: 0 0 auto;
    font-size: 10px;
    color: #2c7a4b;
  }

  .browser-content {
    position: relative;
    width: 100%;
    height: clamp(620px, 72vh, 900px);
    overflow: hidden;
    background: #fff;
  }

  .preview-iframe {
    display: block;
    width: 100%;
    height: 100%;
    margin: 0;
    padding: 0;
    background: #fff;
    border: 0;
  }

  /* ── States ── */
  .state-card {
    display: flex;
    gap: 14px;
    align-items: flex-start;
    background: #fff;
    border: 1px solid #e4e9f2;
    border-radius: 8px;
    padding: 16px;
  }

  .state-card__icon {
    flex: 0 0 auto;
    font-size: 20px;
    line-height: 1.2;
    color: #5f79c7;
  }

  .state-card__text {
    min-width: 0;
  }

  .state-card__title {
    margin-bottom: 3px;
    font-size: 14px;
    font-weight: 600;
    color: #1f2a44;
  }

  .state-card__message {
    font-size: 13px;
    line-height: 1.6;
    color: #7b8397;
  }

  .state-card--warning {
    background: #fdf6e3;
    border-color: #efdca8;
  }

  .state-card--warning .state-card__icon { color: #a5822a; }
  .state-card--warning .state-card__title { color: #7a5a12; }
  .state-card--warning .state-card__message { color: #8a6c28; }

  .state-card--error {
    background: #fdf0ec;
    border-color: #f3cec2;
  }

  .state-card--error .state-card__icon { color: #a4472f; }
  .state-card--error .state-card__title { color: #8c2f1d; }
  .state-card--error .state-card__message { color: #9c4b37; }

  /* ── Kendo overrides (unchanged behaviour, retuned to 2.0) ── */
  .lo-site-notification-group {
    position: fixed;
    right: 24px;
    bottom: 5px;
    z-index: 30000;
  }

  :deep(.lo-site-notification-group .k-notification-success) {
    display: flex;
    gap: 10px;
    align-items: center;
    justify-content: center;
    background-color: var(--primary);
    border-bottom-color: var(--primary);
  }

  :deep(.lo-site-error-dialog .k-dialog) {
    width: min(520px, calc(100vw - 32px));
    border-radius: 12px;
    overflow: hidden;
  }

  :deep(.lo-site-error-dialog .k-dialog-titlebar) {
    padding: 20px 16px;
    background-color: var(--error) !important;
    color: var(--on-error) !important;
    border-bottom: unset !important;
    display: flex;
    align-items: center;
  }

  :deep(.lo-site-error-dialog .k-dialog .k-svg-icon) {
    color: var(--background) !important;
  }

  .error-dialog-message {
    margin: 10px;
    text-align: center;
    font-size: 13px;
    line-height: 1.6;
    color: #3c4658;
  }

  /* ── Responsive ── */
  @media (max-width: 900px) {
    .lo-site-page {
      padding: 14px;
    }

    .lo-card__head {
      flex-wrap: wrap;
      row-gap: 8px;
    }

    .preview-actions {
      margin-left: auto;
    }
  }

  @media (max-width: 620px) {
    .preview-url {
      flex-basis: 100%;
    }

    .browser-content {
      height: clamp(420px, 60vh, 640px);
    }
  }

  @media (prefers-reduced-motion: reduce) {
    .template-option {
      transition: none;
    }

    .template-option:hover {
      transform: none;
    }
  }
</style>
